import { Component, Inject, OnDestroy, OnInit } from "@angular/core";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import {
  CreateUpdateDisputePartyBaseDto,
  DisputeCaseBaseDto,
  DisputeCaseType,
  DisputePartyBaseDto,
} from "src/app/shared/models/dispute-resolution/dispute-base-models";
import { NotificationService } from "src/app/shared/services/notification.service";
import {
  DataListDisputeDueProcessFiledBy,
  DataListDisputeDueProcessRespondingParty,
  DataListRelationship,
} from "src/app/shared/models/data-list";
import { DataListService } from "src/app/shared/services/data-list.service";
import {
  CreateUpdateDueProcessPartyDto,
  DueProcessDto,
} from "src/app/shared/models/dispute-resolution/due-process-models";
import { DueProcessService } from "src/app/shared/services/dispute-resolution/due-process.service";
import { CreateUpdateMediationPartyDto } from "src/app/shared/models/dispute-resolution/mediation-models";
import { CreateUpdateAreaOfConcernPartyDto } from "src/app/shared/models/dispute-resolution/area-of-concern-models";
import { CreateUpdateStateComplaintPartyDto } from "src/app/shared/models/dispute-resolution/state-complaint-models";
import { CreateUpdateIEPFacilitationPartyDto } from "src/app/shared/models/dispute-resolution/iep-facilitation-models";
import { CreateUpdateParentEngagementPartyDto } from "src/app/shared/models/dispute-resolution/parent-engagement-models";
import { firstValueFrom, Subscription } from "rxjs";

@Component({
  selector: "app-add-edit-party-information",
  templateUrl: "./add-edit-party-information.component.html",
  styleUrls: ["./add-edit-party-information.component.scss"],
  standalone: false,
})
export class AddEditPartyInformationComponent implements OnInit, OnDestroy {
  DisputeCaseType = DisputeCaseType;

  form: FormGroup;

  disputeCase: DisputeCaseBaseDto;
  relationshipOptions: DataListRelationship[];
  party: DisputePartyBaseDto;

  isEditMode = false;

  // Additional fields for Due Process
  complaintFiledByOptions: DataListDisputeDueProcessFiledBy[] = [];
  filedByOtherOptionId: string | undefined;
  respondingPartyOptions: DataListDisputeDueProcessRespondingParty[] = [];
  respondingPartyOtherOptionId: string | undefined;
  // end of additional fields

  private subscriptions = new Subscription();

  constructor(
    private notificationService: NotificationService,
    private dataListService: DataListService,
    private dueProcessService: DueProcessService,
    private fb: FormBuilder,
    public dialogRef: MatDialogRef<AddEditPartyInformationComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { disputeCase: DisputeCaseBaseDto; relationshipOptions: DataListRelationship[]; party: DisputePartyBaseDto }
  ) {
    this.disputeCase = data.disputeCase || null;
    this.relationshipOptions = data.relationshipOptions || [];
    this.party = data.party || null;
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  ngOnInit() {
    this.isEditMode = this.party && this.party.id ? true : false;

    this.form = this.fb.group({
      role: [this.party?.relationshipId || undefined],
      firstName: [this.party?.firstName || ""],
      lastName: [this.party?.lastName || ""],
      phone: [this.party?.phone || ""],
      email: [this.party?.email || "", Validators.email],
      partyStreet1: [this.party?.street1 || ""],
      partyStreet2: [this.party?.street2 || ""],
      partyCity: [this.party?.city || ""],
      partyState: [this.party?.state || ""],
      partyZip: [this.party?.zip || ""],
      isFiler: [this.party?.isFiler || false],
      isRespondent: [this.party?.isRespondent || false],
      // Seeded for every type, shown or not, so a save never blanks the stored value.
      relationshipOther: [
        this.party?.relationshipOther ?? null,
        this.showRelationshipToChild ? Validators.required : [],
      ],
    });

    // ALJ parties must have an email and a name (needed to create/link and greet
    // the DPCon user). These are required only while the ALJ relationship is selected.
    const applyAljValidators = () => {
      const emailCtrl = this.form.get("email");
      emailCtrl?.setValidators(
        this.isAljSelected
          ? [Validators.required, Validators.email]
          : [Validators.email]
      );
      emailCtrl?.updateValueAndValidity({ emitEvent: false });

      for (const controlName of ["firstName", "lastName"]) {
        const ctrl = this.form.get(controlName);
        ctrl?.setValidators(this.isAljSelected ? [Validators.required] : []);
        ctrl?.updateValueAndValidity({ emitEvent: false });
      }
    };
    applyAljValidators();
    this.subscriptions.add(
      this.form.get("role")!.valueChanges.subscribe(() => applyAljValidators())
    );

    this.loadComponentsByDisputeCaseType();
  }

  /**
   * The requester's free-text "Relationship to Child" (stored in relationshipOther) is
   * editable here only for types with no other editor for it. Parent Engagement and EDR
   * edit it on the page itself, which overwrites the filer's value on save.
   */
  get showRelationshipToChild(): boolean {
    return (
      this.isEditMode &&
      !!this.party?.isFiler &&
      [
        DisputeCaseType.Mediation,
        DisputeCaseType.IEPFacilitation,
        DisputeCaseType.AreaOfConcern,
      ].includes(this.disputeCase?.disputeCaseType)
    );
  }

  /** The Relationship option whose label is "ALJ" (Due Process only), if present. */
  private get aljRelationshipId(): string | undefined {
    if (this.disputeCase?.disputeCaseType !== DisputeCaseType.DueProcess) {
      return undefined;
    }
    return this.relationshipOptions?.find(
      (r) => r.label?.trim().toLowerCase() === "alj"
    )?.id;
  }

  /** True when the currently selected relationship is ALJ. */
  get isAljSelected(): boolean {
    const aljId = this.aljRelationshipId;
    return !!aljId && this.form?.get("role")?.value === aljId;
  }

  onCancel() {
    this.dialogRef.close();
  }

  async onSave() {
    if (!this.form.valid) {
      this.form.markAllAsTouched();
      this.notificationService.error("Please fix errors before saving.");
      return;
    }

    // Check if all form fields are empty before mapping
    const values = this.form.getRawValue();
    const isEmpty = Object.values(values).every(
      (v) => v === undefined || v === null || v === "" || v === false
    );
    if (isEmpty) {
      this.notificationService.error(
        "Cannot save an empty party. Please fill out at least one field."
      );
      return;
    }

    const partyData = this.mapPartyPayloadFromForm();

    // ALJ parties provision a DPCon user when the case is saved. Decide here
    // whether to confirm with the user (existing non-DPCon) or just inform (new user).
    if (this.isAljSelected) {
      const email = (this.form.get("email")?.value || "").trim();
      if (email) {
        const proceed = await this.confirmAljParty(
          email,
          partyData as CreateUpdateDueProcessPartyDto
        );
        if (!proceed) {
          return; // user cancelled — keep the dialog open
        }
      }
    }

    this.dialogRef.close(partyData);
  }

  // Returns true if the save should proceed. Shows the appropriate add-time modal
  // based on the prospective user's status; stamps confirmAddDpconRole when an
  // existing non-DPCon user is confirmed. Provisioning itself happens on case save.
  private async confirmAljParty(
    email: string,
    partyData: CreateUpdateDueProcessPartyDto
  ): Promise<boolean> {
    const status = await firstValueFrom(
      this.dueProcessService.getAljUserStatus(email)
    );

    // Existing user already has DPCon — no modal, just proceed.
    if (status.exists && status.hasDpcon) {
      return true;
    }

    // Existing user without DPCon — confirm granting it on save.
    if (status.exists && !status.hasDpcon) {
      const roles = status.currentRoleCodes?.length
        ? status.currentRoleCodes.join(", ")
        : "no roles";
      const confirmed = await this.notificationService.confirmationPromise(
        `This user is assigned "${roles}". When you save the case, they'll also be granted the DPCon role and added to this case. Are you sure you want to add this user?`
      );
      if (confirmed) {
        partyData.confirmAddDpconRole = true;
      }
      return confirmed;
    }

    // New user — inform that an account + invite will be created on save.
    return await this.notificationService.confirmationPromise(
      `A DPCon account will be created and an invite email sent to ${email} when you save the case.`
    );
  }

  private loadComponentsByDisputeCaseType() {
    if (this.disputeCase.disputeCaseType === DisputeCaseType.DueProcess) {
      if (this.isEditMode) {
        if (this.party.isFiler) {
          this.form.addControl(
            "isFiler",
            this.fb.control(this.party.isFiler, Validators.required)
          );
          this.form.get("isFiler")?.disable();

          this.form.addControl(
            "complaintFiledBy",
            this.fb.control(
              (this.disputeCase as DueProcessDto).disputeDueProcessFiledById,
              Validators.required
            )
          );

          this.form.addControl(
            "complaintFiledByOther",
            this.fb.control(
              (this.disputeCase as DueProcessDto)
                .disputeDueProcessFiledByOther || ""
            )
          );

          // Watch for changes in "Complaint Filed By" and "Responding Party"
          this.subscriptions.add(this.form.get("complaintFiledBy")?.valueChanges.subscribe((value) => {
            this.form.get("complaintFiledByOther")?.setValue("");
            const otherId = this.complaintFiledByOptions.find(
              (option) => option.label === "Other"
            )?.id;
            if (value === otherId) {
              this.form
                .get("complaintFiledByOther")
                ?.setValidators(Validators.required);
            } else {
              this.form.get("complaintFiledByOther")?.clearValidators();
            }
            this.form.get("complaintFiledByOther")?.updateValueAndValidity();
          }));

          this.dataListService
            .getDisputeDueProcessFiledBies({
              pageIndex: 0,
              pageSize: 100,
            })
            .subscribe({
              next: (complaintFiledByOptions) => {
                this.complaintFiledByOptions = complaintFiledByOptions;
                this.filedByOtherOptionId = this.complaintFiledByOptions.find(
                  (option) => option.label === "Other"
                )?.id;
              },
              error: () => {
                this.notificationService.error(
                  "Failed to load complaint filed by options."
                );
              },
            });
        }
        if (this.party.isRespondent) {
          this.form.addControl(
            "isRespondent",
            this.fb.control(this.party.isRespondent, Validators.required)
          );
          this.form.get("isRespondent")?.disable();

          this.form.addControl(
            "respondingParty",
            this.fb.control(
              (this.disputeCase as DueProcessDto).disputeRespondingPartyId,
              Validators.required
            )
          );

          this.form.addControl(
            "respondingPartyOther",
            this.fb.control(
              (this.disputeCase as DueProcessDto).disputeRespondingPartyOther ||
              ""
            )
          );

          this.subscriptions.add(this.form.get("respondingParty")?.valueChanges.subscribe((value) => {
            this.form.get("respondingPartyOther")?.setValue("");
            const otherId = this.respondingPartyOptions.find(
              (option) => option.label === "Other"
            )?.id;
            if (value === otherId) {
              this.form
                .get("respondingPartyOther")
                ?.setValidators(Validators.required);
            } else {
              this.form.get("respondingPartyOther")?.clearValidators();
            }
            this.form.get("respondingPartyOther")?.updateValueAndValidity();
          }));

          this.dataListService
            .getDisputeDueProcessRespondingParty({
              pageIndex: 0,
              pageSize: 100,
            })
            .subscribe({
              next: (respondingPartyOptions) => {
                this.respondingPartyOptions = respondingPartyOptions;
                this.respondingPartyOtherOptionId =
                  this.respondingPartyOptions.find(
                    (option) => option.label === "Other"
                  )?.id;
              },
              error: () => {
                this.notificationService.error(
                  "Failed to load responding party options."
                );
              },
            });
        }
      }
    }

    if (
      this.disputeCase.disputeCaseType === DisputeCaseType.Mediation ||
      this.disputeCase.disputeCaseType === DisputeCaseType.AreaOfConcern ||
      this.disputeCase.disputeCaseType === DisputeCaseType.StateComplaint ||
      this.disputeCase.disputeCaseType === DisputeCaseType.IEPFacilitation ||
      this.disputeCase.disputeCaseType === DisputeCaseType.ParentEngagement
    ) {
      if (this.isEditMode) {
        if (this.party.isFiler) {
          this.form.addControl(
            "isFiler",
            this.fb.control(this.party.isFiler, Validators.required)
          );
          this.form.get("isFiler")?.disable();
        }
      }
    }
  }

  private mapPartyPayloadFromForm(): CreateUpdateDisputePartyBaseDto {
    if (this.disputeCase.disputeCaseType === DisputeCaseType.DueProcess) {
      return {
        id: this.party?.id || undefined,
        disputeCaseId: this.disputeCase.id,
        relationshipId: this.form.get("role")?.value,
        firstName: this.form.get("firstName")?.value,
        lastName: this.form.get("lastName")?.value,
        phone: this.form.get("phone")?.value,
        email: this.form.get("email")?.value,
        street1: this.form.get("partyStreet1")?.value,
        street2: this.form.get("partyStreet2")?.value,
        city: this.form.get("partyCity")?.value,
        state: this.form.get("partyState")?.value,
        zip: this.form.get("partyZip")?.value,
        isFiler: this.form.get("isFiler")?.value,
        isRespondent: this.form.get("isRespondent")?.value,
        relationshipOther: this.form.get("relationshipOther")?.value,
        disputeDueProcessFiledById: this.form.get("complaintFiledBy")?.value,
        disputeDueProcessFiledByOther: this.form.get("complaintFiledByOther")
          ?.value,
        disputeRespondingPartyId: this.form.get("respondingParty")?.value,
        disputeRespondingPartyOther: this.form.get("respondingPartyOther")
          ?.value,
      } as CreateUpdateDueProcessPartyDto;
    }

    if (this.disputeCase.disputeCaseType === DisputeCaseType.Mediation) {
      return {
        id: this.party?.id || undefined,
        disputeCaseId: this.disputeCase.id,
        relationshipId: this.form.get("role")?.value,
        firstName: this.form.get("firstName")?.value,
        lastName: this.form.get("lastName")?.value,
        phone: this.form.get("phone")?.value,
        email: this.form.get("email")?.value,
        street1: this.form.get("partyStreet1")?.value,
        street2: this.form.get("partyStreet2")?.value,
        city: this.form.get("partyCity")?.value,
        state: this.form.get("partyState")?.value,
        zip: this.form.get("partyZip")?.value,
        isFiler: this.form.get("isFiler")?.value,
        isRespondent: this.form.get("isRespondent")?.value,
        relationshipOther: this.form.get("relationshipOther")?.value,
      } as CreateUpdateMediationPartyDto;
    }

    if (this.disputeCase.disputeCaseType === DisputeCaseType.AreaOfConcern) {
      return {
        id: this.party?.id || undefined,
        disputeCaseId: this.disputeCase.id,
        relationshipId: this.form.get("role")?.value,
        firstName: this.form.get("firstName")?.value,
        lastName: this.form.get("lastName")?.value,
        phone: this.form.get("phone")?.value,
        email: this.form.get("email")?.value,
        street1: this.form.get("partyStreet1")?.value,
        street2: this.form.get("partyStreet2")?.value,
        city: this.form.get("partyCity")?.value,
        state: this.form.get("partyState")?.value,
        zip: this.form.get("partyZip")?.value,
        isFiler: this.form.get("isFiler")?.value,
        isRespondent: this.form.get("isRespondent")?.value,
        relationshipOther: this.form.get("relationshipOther")?.value,
      } as CreateUpdateAreaOfConcernPartyDto;
    }

    if (this.disputeCase.disputeCaseType === DisputeCaseType.StateComplaint) {
      return {
        id: this.party?.id || undefined,
        disputeCaseId: this.disputeCase.id,
        relationshipId: this.form.get("role")?.value,
        firstName: this.form.get("firstName")?.value,
        lastName: this.form.get("lastName")?.value,
        phone: this.form.get("phone")?.value,
        email: this.form.get("email")?.value,
        street1: this.form.get("partyStreet1")?.value,
        street2: this.form.get("partyStreet2")?.value,
        city: this.form.get("partyCity")?.value,
        state: this.form.get("partyState")?.value,
        zip: this.form.get("partyZip")?.value,
        isFiler: this.form.get("isFiler")?.value,
        isRespondent: this.form.get("isRespondent")?.value,
        relationshipOther: this.form.get("relationshipOther")?.value,
      } as CreateUpdateStateComplaintPartyDto;
    }

    if (this.disputeCase.disputeCaseType === DisputeCaseType.IEPFacilitation) {
      return {
        id: this.party?.id || undefined,
        disputeCaseId: this.disputeCase.id,
        relationshipId: this.form.get("role")?.value,
        firstName: this.form.get("firstName")?.value,
        lastName: this.form.get("lastName")?.value,
        phone: this.form.get("phone")?.value,
        email: this.form.get("email")?.value,
        street1: this.form.get("partyStreet1")?.value,
        street2: this.form.get("partyStreet2")?.value,
        city: this.form.get("partyCity")?.value,
        state: this.form.get("partyState")?.value,
        zip: this.form.get("partyZip")?.value,
        isFiler: this.form.get("isFiler")?.value,
        isRespondent: this.form.get("isRespondent")?.value,
        relationshipOther: this.form.get("relationshipOther")?.value,
      } as CreateUpdateIEPFacilitationPartyDto;
    }

    if (this.disputeCase.disputeCaseType === DisputeCaseType.ParentEngagement) {
      return {
        id: this.party?.id || undefined,
        disputeCaseId: this.disputeCase.id,
        relationshipId: this.form.get("role")?.value,
        firstName: this.form.get("firstName")?.value,
        lastName: this.form.get("lastName")?.value,
        phone: this.form.get("phone")?.value,
        email: this.form.get("email")?.value,
        street1: this.form.get("partyStreet1")?.value,
        street2: this.form.get("partyStreet2")?.value,
        city: this.form.get("partyCity")?.value,
        state: this.form.get("partyState")?.value,
        zip: this.form.get("partyZip")?.value,
        isFiler: this.form.get("isFiler")?.value,
        isRespondent: this.form.get("isRespondent")?.value,
        relationshipOther: this.form.get("relationshipOther")?.value,
      } as CreateUpdateParentEngagementPartyDto;
    }
  }
}
